import Component from '@glimmer/component';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import parseMarkdown from '../helpers/parse-markdown';
import parseHintedMarkdown, { type ItemHint } from '../helpers/hinted-markdown';
import scrollToMention from '../modifiers/scroll-to-mention';
import type { Mention } from '../schemas/mention';

interface HintedSummarySignature {
  Args: {
    text: string | null;
    mentions: Mention[];
    mentionId?: string;
  };
}

/**
 * Extract the enriched title from an item's enrichedData.
 * Falls back to the item's name if no enriched title exists.
 */
function getEnrichedTitle(item: Mention['item']): string {
  if (!item) return '';

  const enriched = item.enrichedData as
    { title?: string; name?: string } | null | undefined;

  return enriched?.title || enriched?.name || item.name || '';
}

/**
 * Extract the original extracted name from an item's enrichedData.
 * This is stored in enrichedData.extractedName and represents the name
 * as originally transcribed/extracted before any corrections.
 */
function getExtractedName(item: Mention['item']): string {
  if (!item) return '';

  const enriched = item.enrichedData as
    { extractedName?: string } | null | undefined;

  // extractedName is set when the item was created, representing the original extraction
  return enriched?.extractedName || item.name || '';
}

export default class HintedSummary extends Component<HintedSummarySignature> {
  @service declare router: RouterService;

  /**
   * Extracts unique item hints from mentions.
   * Uses the enriched title for display and searching, but also tracks
   * the original extracted name for find+replace corrections.
   */
  get itemHints(): ItemHint[] {
    try {
      const { mentions } = this.args;
      if (!mentions || mentions.length === 0) {
        return [];
      }

      const seenItems = new Set<string>();
      const hints: ItemHint[] = [];

      for (const mention of mentions) {
        const item = mention.item;
        if (!item) {
          continue;
        }

        const enrichedTitle = getEnrichedTitle(item);
        const extractedName = getExtractedName(item);

        if (!enrichedTitle) {
          continue;
        }

        // Skip duplicate items (same enriched title and type)
        const key = `${enrichedTitle}|${item.type || 'unknown'}`;
        if (seenItems.has(key)) {
          continue;
        }
        seenItems.add(key);

        hints.push({
          id: mention.id!,
          name: enrichedTitle, // Use enriched title for display and search
          extractedName:
            extractedName !== enrichedTitle ? extractedName : undefined, // Track original if different
          type: item.type ?? 'item',
        });
      }

      return hints;
    } catch {
      // If hint extraction fails, return empty array
      return [];
    }
  }

  /**
   * Parses markdown and applies item hints via a custom Marked extension.
   */
  get hintedHtml(): ReturnType<typeof parseHintedMarkdown> {
    try {
      return parseHintedMarkdown(this.args.text, this.itemHints);
    } catch {
      // If hinting fails, fall back to plain markdown
      return parseMarkdown(this.args.text);
    }
  }

  <template>
    <div
      class="prose prose-sm dark:prose-invert text-base-content/80 max-w-none"
      {{scrollToMention router=this.router mentionId=@mentionId}}
    >
      {{this.hintedHtml}}
    </div>
  </template>
}
